import { ChangeEvent, useMemo, useState } from 'react';
import { QTabContext, QTabList, QTab, QTabPanel, QContainer, QDateRange, QTypography } from '@kw/quokka-ui';
import { Box } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import moment from 'moment';
import { AppInventory } from './AppInventory';
import { DashboardAnalyticsDateRangeDays } from './dashboardAnalytics';
import { RiskInsights } from './RiskInsights';
import { PolicyViolations } from './PolicyViolations';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useDashboardAnalyticsMetrics } from './useDashboardAnalyticsMetrics';

export const Dashboards = () => {
  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [activeTab, setActiveTab] = useState<number>(0);
  const [dateRangeDays, setDateRangeDays] = useState<DashboardAnalyticsDateRangeDays>(90);

  const { data: metrics } = useDashboardAnalyticsMetrics(dateRangeDays, accessToken);

  const lastRefreshedLabel = useMemo(() => {
    if (metrics?.updatedAt) {
      return moment(metrics.updatedAt).format('MMMM D, YYYY [at] h:mma');
    }
    return '—';
  }, [metrics?.updatedAt]);

  const handleChange = (_event: ChangeEvent<HTMLButtonElement>, tab: number) => {
    setActiveTab(tab);
  };

  const handleDateRangeChange = (event: SelectChangeEvent<number>) => {
    setDateRangeDays(event.target.value as DashboardAnalyticsDateRangeDays);
  };

  return (
    <QContainer maxWidth={false}>
      <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'} mb={5}>
        <Box width={178}>
          <QDateRange
            data-testid="date-range-select"
            formControlProps={{ fullWidth: true }}
            value={dateRangeDays}
            onChange={handleDateRangeChange}
          />
        </Box>
        <QTypography variant="body">Last refreshed: {lastRefreshedLabel}</QTypography>
      </Box>
      <QTabContext value={activeTab}>
        <QTabList tabs="true" onChange={handleChange}>
          <QTab data-testid="app-inventory-tab" label="App Inventory" value={0} />
          <QTab data-testid="risk-insights-tab" label="Risk Insights" value={1} />
          <QTab data-testid="policy-violations-tab" label="Policy Violations" value={2} />
        </QTabList>
        <QTabPanel tabid="0" value={0} index={0} sx={{ paddingLeft: '0px', paddingTop: '40px' }}>
          <AppInventory dateRangeDays={dateRangeDays} metrics={metrics} />
        </QTabPanel>
        <QTabPanel tabid="1" value={1} index={1} sx={{ paddingLeft: '0px', paddingTop: '40px' }}>
          <RiskInsights dateRangeDays={dateRangeDays} metrics={metrics} />
        </QTabPanel>
        <QTabPanel tabid="2" value={2} index={2} sx={{ paddingLeft: '0px', paddingTop: '40px' }}>
          <PolicyViolations dateRangeDays={dateRangeDays} metrics={metrics} />
        </QTabPanel>
      </QTabContext>
    </QContainer>
  );
};
